import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePageChrome } from '@/components/layout/page-chrome';
import { useSession, hasCapability } from '@/app/session/session';
import { parseCalendarSearch } from '@/shared/url/calendar-search-params';
import { ExportCentre, type ExportRowConfig } from './ExportCentre';
import {
  toFilteredViewRequest,
  useRequestFilteredViewExport,
  useRequestFullBackupExport,
} from './export-centre-data';

/**
 * The Export centre route (`/reports`), task 23.5.
 *
 * The screen presents the §3.5 export table with the two Foundation Excel rows —
 * full backup and filtered view — each of which enqueues an export job the centre
 * polls at least every five seconds (R18.4), showing progress while it runs and
 * offering a download on success or stating that no file was produced and the
 * stored data is unchanged on failure/timeout (R14.11, R15.9, R18.17).
 *
 * Capability gating (R2.5): the full-backup row renders only for a principal that
 * holds `export:full` (Commercial Planning); the filtered-view row renders for any
 * holder of `export:filtered` (every reading role). A principal with neither sees
 * an empty table stating nothing is available to them, mirroring the server, which
 * denies the corresponding POST regardless of what the client renders.
 *
 * Foundation ships no filter bar on this screen; the filtered-view export uses the
 * default calendar view for the current planning year (an unfiltered view of what
 * the requesting user may read). This is flagged in the task report: the click
 * dummy's export centre carries a static filter context, and R15's screen-agreement
 * guarantee (Property 8) is exercised by the calendar's own export (task 22.6),
 * which carries the live filter state. The centre's row is the "export everything
 * in the default view" entry point.
 */
export function ExportCentrePage(): React.JSX.Element {
  const { t } = useTranslation();
  const session = useSession();
  const me = session.data;

  usePageChrome({
    title: t('exportCentre.page.title'),
    subtitle: t('exportCentre.page.subtitle'),
  });

  const currentYear = new Date().getFullYear();

  const fullBackupMutation = useRequestFullBackupExport();
  const filteredViewMutation = useRequestFilteredViewExport();

  // The default (unfiltered) calendar view for the current planning year — the
  // filter context the centre's filtered-view export runs under.
  const defaultView = useMemo(
    () => parseCalendarSearch(currentYear, new URLSearchParams()),
    [currentYear],
  );

  const canFullBackup = hasCapability(me, 'export:full');
  const canFilteredView = hasCapability(me, 'export:filtered');

  const rows = useMemo<ExportRowConfig[]>(() => {
    const list: ExportRowConfig[] = [];

    if (canFullBackup) {
      list.push({
        id: 'full_backup',
        format: t('exportCentre.fullBackup.format'),
        content: t('exportCentre.fullBackup.content'),
        controls: t('exportCentre.fullBackup.controls'),
        start: (onStarted) => {
          fullBackupMutation.mutate({}, { onSuccess: onStarted });
        },
        isStarting: fullBackupMutation.isPending,
        startFailed: fullBackupMutation.isError,
      });
    }

    if (canFilteredView) {
      list.push({
        id: 'filtered_view',
        format: t('exportCentre.filteredView.format'),
        content: t('exportCentre.filteredView.content'),
        controls: t('exportCentre.filteredView.controls'),
        start: (onStarted) => {
          filteredViewMutation.mutate(
            toFilteredViewRequest(defaultView.query, defaultView.sort),
            { onSuccess: onStarted },
          );
        },
        isStarting: filteredViewMutation.isPending,
        startFailed: filteredViewMutation.isError,
      });
    }

    return list;
  }, [
    canFullBackup,
    canFilteredView,
    fullBackupMutation,
    filteredViewMutation,
    defaultView,
    t,
  ]);

  const contextChips = useMemo(
    () => [
      t('exportCentre.context.year', { year: currentYear }),
      t('exportCentre.context.scope'),
    ],
    [currentYear, t],
  );

  return (
    <div className="flex flex-col gap-4">
      <ExportCentre rows={rows} contextChips={contextChips} />
    </div>
  );
}
